import { useMemo } from 'react'
import { useAlbumPhotosQuery } from '@/api/hooks/use-album-photos'
import { usePhotoSearchQuery } from '@/api/hooks/use-photo-search'
import { useDeletePhotoRecommendation } from '@/api/hooks/use-delete-photo-recommendation'
import { usePhotoPlacementOverrides } from '@/api/hooks/use-photo-placement-overrides'
import {
  decodeAlbumSelection,
  shouldScopeFilenameSearchToAlbum,
} from '@/lib/photos/album-selection'
import { type PhotoTrayFilters, filterPhotosForTray } from '@/lib/photos/filter-photos'
import { resolveFilenameSearchPhotos } from '@/lib/photos/filename-search'
import { type PhotosTraySort, sortTrayPhotos } from '@/lib/photos/sort-tray-photos'
import { applyPhotoPlacementOverrides } from '@/lib/photos/photo-placement-session'

type PhotosTrayPhotosInput = {
  albumValue: string | null
  spreadId: string | null
  filenameQuery: string
  isFilenameSearch: boolean
  trayFilters: PhotoTrayFilters
  traySort: PhotosTraySort
}

/**
 * The Photos tray's results: the selected album's photos (or, while a filename search is
 * active, the search results), with this session's placement overrides applied, then filtered
 * and sorted for display.
 */
export function usePhotosTrayPhotos({
  albumValue,
  spreadId,
  filenameQuery,
  isFilenameSearch,
  trayFilters,
  traySort,
}: PhotosTrayPhotosInput) {
  const albumSelection = decodeAlbumSelection(albumValue)
  const hasAlbumSelected = Boolean(albumSelection)
  const isFavorites = albumSelection?.type === 'curated' && albumSelection.key === 'favorites'
  const isRecommendToMe =
    albumSelection?.type === 'curated' && albumSelection.key === 'recommend-to-me'
  const isRecommendForSpread =
    albumSelection?.type === 'curated' && albumSelection.key === 'recommended'
  const placementOverrides = usePhotoPlacementOverrides()
  const removeRecommendation = useDeletePhotoRecommendation(albumValue, spreadId)

  const {
    data: albumPhotos = [],
    isLoading: isPhotosLoading,
    isError: isPhotosError,
    error: photosError,
  } = useAlbumPhotosQuery(albumValue, spreadId)

  const {
    data: searchPhotos = [],
    isLoading: isSearchLoading,
    isError: isSearchError,
  } = usePhotoSearchQuery(filenameQuery)

  const filenameSearchPhotos = useMemo(
    () =>
      resolveFilenameSearchPhotos(searchPhotos, albumPhotos, filenameQuery, {
        scopeToAlbum: shouldScopeFilenameSearchToAlbum(albumSelection),
      }),
    [albumPhotos, albumSelection, filenameQuery, searchPhotos]
  )

  const sourcePhotos = isFilenameSearch ? filenameSearchPhotos : albumPhotos
  const isTrayLoading = isFilenameSearch ? isSearchLoading : isPhotosLoading
  const isTrayError = isFilenameSearch ? isSearchError : isPhotosError
  const canShowTray = hasAlbumSelected || isFilenameSearch
  // The tray is showing results: something to show, and it is neither loading nor errored.
  const canShowResults = canShowTray && !isTrayLoading && !isTrayError

  const trayPhotos = useMemo(
    () => applyPhotoPlacementOverrides(sourcePhotos, placementOverrides),
    [placementOverrides, sourcePhotos]
  )

  const filteredPhotos = useMemo(
    () => filterPhotosForTray(trayPhotos, trayFilters),
    [trayFilters, trayPhotos]
  )

  const displayPhotos = useMemo(
    () => sortTrayPhotos(filteredPhotos, traySort),
    [filteredPhotos, traySort]
  )

  return {
    hasAlbumSelected,
    isFavorites,
    isRecommendToMe,
    isRecommendForSpread,
    removeRecommendation,
    albumPhotos,
    isPhotosLoading,
    isPhotosError,
    photosError,
    isTrayLoading,
    isTrayError,
    canShowTray,
    canShowResults,
    displayPhotos,
  }
}
